import { getLocalizedText } from './localization';

import type { Company, CompanyAttribute, CompanyManager } from '@/types/directory/Company';

import i18n from '@/plugins/i18n';

export type CompanyAttributeMap = Record<
    string,
    { name: string; values: CompanyAttribute[] | CompanyManager[]; iconName: string }
>;

export const getCompanyAttributeList = (company?: Company): CompanyAttributeMap => {
    if (!company) {
        return {};
    }

    const { t } = i18n;

    const attributeMap: CompanyAttributeMap = {
        departments: {
            name: t('common.department'),
            values: (company.departments ?? []).map((dept) => ({
                ...dept,
                name: getLocalizedText(dept.names),
            })),
            iconName: 'building-building-4',
        },
        jobTitles: {
            name: t('common.jobTitle'),
            values: (company.jobTitles ?? []).map((job) => ({
                ...job,
                name: getLocalizedText(job.names),
            })),
            iconName: 'school-briefcase',
        },
        companyAddresses: {
            name: t('common.office'),
            values: (company.companyAddresses ?? []).map((address) => ({
                ...address,
                name: getLocalizedText(address.names),
            })),
            iconName: 'location-location',
        },
        employeeLevels: {
            name: t('common.employeeLevel'),
            values: (company.employeeLevels ?? []).map((level) => ({
                ...level,
                name: getLocalizedText(level.names),
            })),
            iconName: 'design-layer',
        },
        ageGroups: {
            name: t('common.ageGroup'),
            values: (company.ageGroups ?? []).map((age) => ({
                ...age,
                name: getLocalizedText(age.names),
            })),
            iconName: 'users-people',
        },
        tenures: {
            name: t('common.tenure'),
            values: (company.tenures ?? []).map((tenure) => ({
                ...tenure,
                name: getLocalizedText(tenure.names),
            })),
            iconName: 'time-timer',
        },
        managers: {
            name: t('common.manager'),
            values: (company.managers ?? []).map((manager) => ({
                ...manager,
                name: manager.name,
            })),
            iconName: 'users-user',
        },
    };

    company.customAttributes?.forEach((customAttr) => {
        attributeMap[customAttr.id] = {
            name: getLocalizedText(customAttr.names),
            values: customAttr.values,
            iconName: 'school-bookmark',
        };
    });

    company.attributes?.forEach((attribute) => {
        const rawAttribute = attribute as unknown as {
            key?: string;
            id?: string;
            iconName?: string;
            name?: Record<string, string> | string;
            names?: Record<string, string>;
            values?: Array<{
                id: string;
                name?: Record<string, string> | string;
                names?: Record<string, string>;
            }>;
        };
        const attributeKey = rawAttribute.key ?? rawAttribute.id;
        if (!attributeKey) {
            return;
        }

        const attributeNames =
            rawAttribute.name && typeof rawAttribute.name === 'object'
                ? rawAttribute.name
                : rawAttribute.names;
        const attributeName =
            typeof rawAttribute.name === 'string'
                ? rawAttribute.name
                : attributeKey === 'managers'
                  ? t('common.manager')
                  : getLocalizedText(attributeNames);

        attributeMap[attributeKey] = {
            name: attributeName,
            values: (rawAttribute.values ?? []).map((value) => {
                const valueNames =
                    value.name && typeof value.name === 'object' ? value.name : value.names;
                const valueName =
                    typeof value.name === 'string' ? value.name : getLocalizedText(valueNames);

                return {
                    id: value.id,
                    names: valueNames,
                    name: valueName,
                };
            }),
            iconName: rawAttribute.iconName ?? 'school-bookmark',
        };
    });

    return attributeMap;
};
